import type { RepeatMode } from './musicTypes';

/**
 * Порядок воспроизведения: чистые функции, без состояния — их проще проверить, чем провайдер.
 *
 * <p><b>Перемешивание — один раз, а не на каждом шаге.</b> Если на «следующий» тянуть
 * случайный трек, одна песня играет трижды за час, а другая не звучит никогда, и «назад»
 * ведёт не туда, откуда пришли. Поэтому при включении режима очередь тасуется один раз
 * (Фишер — Йетс), текущий трек ставится первым, и дальше по ней ходят как по обычной.
 */

export function shuffleOrder(ids: readonly string[], currentId: string | null, random: () => number = Math.random): string[] {
  const rest = ids.filter((id) => id !== currentId);
  for (let i = rest.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }
  return currentId && ids.includes(currentId) ? [currentId, ...rest] : rest;
}

/**
 * Какой трек следующий.
 *
 * @param auto трек доиграл сам (а не нажали «вперёд»). Повтор одного трека действует только на
 *     него: нажатая кнопка «вперёд» обязана переключать, иначе она выглядит сломанной.
 * @returns индекс в очереди или `null` — очередь кончилась, надо остановиться.
 */
export function nextIndex(length: number, index: number, repeat: RepeatMode, auto: boolean): number | null {
  if (length === 0) {
    return null;
  }
  if (auto && repeat === 'one') {
    return index;
  }
  if (index + 1 < length) {
    return index + 1;
  }
  return repeat === 'off' && auto ? null : 0;
}

/**
 * Порог «назад»: первые три секунды кнопка ведёт на предыдущий трек, дальше — в начало
 * текущего. Так ведут себя все системные плееры, и иначе нажатие «назад» на середине песни
 * выбрасывало бы из неё.
 */
export const RESTART_THRESHOLD_SEC = 3;

export function previousAction(
  length: number,
  index: number,
  positionSec: number,
  repeat: RepeatMode,
): { kind: 'restart' } | { kind: 'go'; index: number } {
  if (positionSec > RESTART_THRESHOLD_SEC || length <= 1) {
    return { kind: 'restart' };
  }
  if (index > 0) {
    return { kind: 'go', index: index - 1 };
  }
  return repeat === 'off' ? { kind: 'restart' } : { kind: 'go', index: length - 1 };
}

export function cycleRepeat(mode: RepeatMode): RepeatMode {
  return mode === 'off' ? 'all' : mode === 'all' ? 'one' : 'off';
}

export function formatTime(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec < 0) {
    return '0:00';
  }
  const s = Math.floor(totalSec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Подпись качества под названием: «FLAC · 24 бит / 96 кГц», «MP3 · 44,1 кГц». */
export function formatQuality(
  format: { codec: string; sampleRate?: number; bitsPerSample?: number },
  units: { bit: string; khz: string },
): string {
  const parts: string[] = [];
  const khz = format.sampleRate ? `${(format.sampleRate / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} ${units.khz}` : '';
  if (format.bitsPerSample && khz) {
    parts.push(`${format.bitsPerSample} ${units.bit} / ${khz}`);
  } else if (khz) {
    parts.push(khz);
  }
  return [format.codec, ...parts].join(' · ');
}

/**
 * Высоты столбиков «волны» трека. Настоящую огибающую без декодирования всего файла не
 * получить, а декодировать FLAC на 40 МБ ради картинки — расточительно. Поэтому форма —
 * своя у каждого трека, но постоянная (зерно — его id): узнаваемая и не прыгающая при
 * каждом открытии. Живое движение поверх неё даёт спектр того, что играет прямо сейчас.
 */
export function trackWaveShape(id: string, bars: number): number[] {
  let seed = 2166136261;
  for (let i = 0; i < id.length; i += 1) {
    seed = Math.imul(seed ^ id.charCodeAt(i), 16777619) >>> 0;
  }
  const rand = () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  const raw = Array.from({ length: bars }, () => rand());
  // Сглаживание соседями: чистый шум выглядит как помехи, а не как музыка.
  return raw.map((_, i) => {
    const a = raw[Math.max(0, i - 1)];
    const b = raw[i];
    const c = raw[Math.min(bars - 1, i + 1)];
    const v = (a + 2 * b + c) / 4;
    return 0.28 + 0.72 * v;
  });
}
